import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PageHero from '../components/PageHero';
import { FiGrid, FiList } from 'react-icons/fi';
import CourseCard from '../components/CourseCard';
import { SkeletonGrid } from '../components/SkeletonLoader';
import { courseService } from '../services/api';
import ScrollAnimation from '../components/ScrollAnimation';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CoursesContainer = styled.div`
  /* remove offset to avoid white gap below header */
  margin-top: 0;
  overflow-x: hidden;
  width: 100%;
`;

// Replaced by PageHero

const Container = styled.div`
  max-width: ${props => props.theme.breakpoints.wide};
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 0 ${props => props.theme.spacing.md};
  }
`;

// PageHero handles heading/subtitle styling

const Section = styled.section`
  padding: ${props => props.theme.spacing['2xl']} 0 ${props => props.theme.spacing['4xl']};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${props => props.theme.spacing.xl} 0 ${props => props.theme.spacing['3xl']};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing['2xl']} 0;
  }
`;

const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing['3xl']};

  @media (min-width: ${props => props.theme.breakpoints.tablet}) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.lg};
  align-items: center;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: stretch;
    gap: ${props => props.theme.spacing.md};
  }
`;

const FilterSelect = styled.select`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border: 2px solid ${props => props.theme.colors.gray[300]};
  border-radius: ${props => props.theme.borderRadius.lg};
  font-size: ${props => props.theme.fontSizes.base};
  background: ${props => props.theme.colors.light};
  cursor: pointer;
  min-width: 150px;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const ViewToggle = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.xs};
  border: 2px solid ${props => props.theme.colors.gray[300]};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xs};

  /* Hide the view toggle on smaller screens; we auto-select list view there */
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;

const ViewButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: none;
  background: ${props => props.active ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.active ? props.theme.colors.light : props.theme.colors.gray[600]};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  transition: ${props => props.theme.transitions.default};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => props.active ? props.theme.colors.primary : props.theme.colors.gray[100]};
  }

  svg {
    font-size: ${props => props.theme.fontSizes.lg};
  }
`;

const CoursesGrid = styled.div`
  display: grid;
  grid-template-columns: ${props => props.viewMode === 'grid' 
    ? 'repeat(auto-fit, minmax(350px, 1fr))' 
    : '1fr'};
  gap: ${props => props.theme.spacing['2xl']};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.xl};
  }
`;

const ResultCount = styled.p`
  color: ${props => props.theme.colors.gray[600]};
  font-size: ${props => props.theme.fontSizes.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
  text-align: center;
`;

const NoResults = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing['4xl']} 0;
  color: ${props => props.theme.colors.gray[600]};

  h3 {
    font-size: ${props => props.theme.fontSizes['2xl']};
    color: ${props => props.theme.colors.gray[700]};
    margin-bottom: ${props => props.theme.spacing.md};
  }

  p {
    font-size: ${props => props.theme.fontSizes.lg};
  }
`;

const Courses = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('popularity');
  const [viewMode, setViewMode] = useState('grid');
  const [loading, setLoading] = useState(false);
  const [allCourses, setAllCourses] = useState([]);
  const [categories, setCategories] = useState([{ id: 'all', name: 'All Courses', slug: 'all' }]);

  // Fetch categories
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const categoriesRes = await fetch(`${API_BASE_URL}/courses/meta/categories`);
        
        if (categoriesRes.ok) {
          const catData = await categoriesRes.json();
          if (catData.success && catData.data.categories) {
            setCategories(catData.data.categories);
          }
        }
      } catch (error) {
      }
    };

    fetchMetadata();
  }, []);

  // Load courses from backend API whenever filters change
  useEffect(() => {
    const fetchCourses = async () => {
      setLoading(true);
      try {
        // Build query parameters
        const params = {
          sortBy: sortBy === 'popularity' ? 'students' : sortBy === 'price-low' ? 'currentPrice' : sortBy === 'price-high' ? '-currentPrice' : sortBy === 'rating' ? '-rating' : sortBy === 'students' ? '-students' : sortBy === 'title' ? 'title' : sortBy === 'newest' ? '-createdAt' : '-students',
        };

        // Add category filter if not 'all'
        if (selectedCategory !== 'all') {
          params.category = selectedCategory;
        }

        // Fetch courses from backend
        const data = await courseService.getCourses(params);
        setAllCourses(data);
      } catch (error) {
        // eslint-disable-next-line no-console
        setAllCourses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, [selectedCategory, sortBy]);

  // Prefer list view on very small screens for readability
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 768px)');
    const apply = () => {
      if (mq.matches) setViewMode('list');
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  return (
    <CoursesContainer>
      <PageHero
        title="Our Courses"
        subtitle="Discover comprehensive training programs designed to advance your career in technology and digital innovation."
        illustrationSrc="/coursespage.png"
        illustrationAlt="Courses illustration"
        rightHeight="380px"
        rightHeightTablet="320px"
        rightHeightMobile="260px"
        topPaddingTablet="1.25rem"
        topPaddingMobile="2rem"
        compact
      />

      <Section>
        <Container>
          <FilterSection>
            <FilterContainer>
              <FilterSelect
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </FilterSelect>

              <FilterSelect
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="popularity">Most Popular</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="rating">Highest Rated</option>
                <option value="students">Most Students</option>
                <option value="title">Title A-Z</option>
              </FilterSelect>
            </FilterContainer>

            <ViewToggle>
              <ViewButton
                active={viewMode === 'grid'}
                onClick={() => setViewMode('grid')}
              >
                <FiGrid />
              </ViewButton>
              <ViewButton
                active={viewMode === 'list'}
                onClick={() => setViewMode('list')}
              >
                <FiList />
              </ViewButton>
            </ViewToggle>
          </FilterSection>

          <ResultCount>
            Showing {allCourses.length} course{allCourses.length !== 1 ? 's' : ''}
          </ResultCount>

          {loading ? (
            <SkeletonGrid type="course" count={6} />
          ) : allCourses.length > 0 ? (
            <CoursesGrid viewMode={viewMode}>
              {allCourses.map((course, index) => (
                <ScrollAnimation key={course.id} animation="fadeInUp" delay={index * 50}>
                  <CourseCard 
                    course={{
                      ...course,
                      description: course.description,
                      newCourse: course.newCourse,
                      icon: null,
                    }}
                    viewMode={viewMode} 
                  />
                </ScrollAnimation>
              ))}
            </CoursesGrid>
          ) : (
            <ScrollAnimation animation="fadeInUp">
              <NoResults>
                <h3>No courses found</h3>
                <p>Try adjusting your filters to see more courses.</p>
              </NoResults>
            </ScrollAnimation>
          )}
        </Container>
      </Section>
    </CoursesContainer>
  );
};

export default Courses;